import { adapterStaitionForForm } from "../../pages/Map/hooks/utils";
import apiBusStations from "../../api/busStaitions/apiBusStaitions";
import {
    ICityResGet,
    IGetFromOrToResponse,
    IGetParamsGET_FROM_OR_TO,
    IRawAreaList
} from "../../api/busStaitions/types";
import { StopsReturn } from "./types";

/**
 * Удобные опции для вызова сервиса.
 * Позволяют передавать locality/stop вместо сырых NLOCALITY_ID_* / NSTOPPOINT_ID_*.
 */
export interface IGetFromOrToOptions {
    date?: string;            // dDATE
    localityIdFrom?: string;  // NLOCALITY_ID_FROM
    localityIdTo?: string;    // NLOCALITY_ID_TO
    stopPointIdFrom?: string; // NSTOPPOINT_ID_FROM
    stopPointIdTo?: string;   // NSTOPPOINT_ID_TO
    firstRowNum?: number;     // NFIRST_ROWNUM
    lastRowNum?: number;      // NLAST_ROWNUM
    filter?: string;          // CFILTER
}

/**
 * Преобразует удобные опции в параметры API.
 */
function mapOptionsToParams(
    CWHAT: IGetParamsGET_FROM_OR_TO["CWHAT"],
    options: IGetFromOrToOptions = {}
): IGetParamsGET_FROM_OR_TO {
    return {
        CWHAT,
        dDATE: options.date,
        NLOCALITY_ID_FROM: options.localityIdFrom,
        NLOCALITY_ID_TO: options.localityIdTo,
        NSTOPPOINT_ID_FROM: options.stopPointIdFrom,
        NSTOPPOINT_ID_TO: options.stopPointIdTo,
        NFIRST_ROWNUM: options.firstRowNum,
        NLAST_ROWNUM: options.lastRowNum,
        CFILTER: options.filter,
    };
}

/**
 * Базовый метод запроса.
 * @param params — параметры запроса к API
 * @param url — необязательный кастомный URL
 */
async function getFromOrTo(
    params: IGetParamsGET_FROM_OR_TO,
    url?: string
): Promise<IRawAreaList> {
    const res = await apiBusStations.getStaitionsOrCityShipping(params);
    //@ts-ignore
    return res.data; // data: IRawAreaList
}

async function getFromOrToCities(
    params: IGetParamsGET_FROM_OR_TO,
    url?: string
): Promise<ICityResGet[]> {
    const res = await apiBusStations.getStaitionsOrCityShipping(params);
    //@ts-ignore
    return res.data; // data: IRawAreaList
}

// =====================
//  ФУНКЦИИ ПО КЕЙСАМ
// =====================

/**
 * Получить города ОТКУДА (ГОРОДА_ОТКУДА)
 *
 * Используется для поля «Населённый пункт ОТКУДА».
 *
 * Примеры:
 * - getCitiesFrom() — получить все города откуда возможен выезд
 * - getCitiesFrom({ stopPointIdFrom: "123" }) — города, доступные из конкретной остановки
 * - getCitiesFrom({ filter: "Екатеринбург" }) — поиск по строке
 */
async function getCitiesFrom(options?: IGetFromOrToOptions) {
    const params = mapOptionsToParams("ГОРОДА_ОТКУДА", options);
    return getFromOrToCities(params);
}

/**
 * Получить города КУДА (ГОРОДА_КУДА)
 *
 * Используется для поля «Населённый пункт КУДА».
 *
 * Примеры:
 * - getCitiesTo({ localityIdFrom: "5" }) — города, куда можно приехать из города 5
 * - getCitiesTo({ stopPointIdFrom: "100" }) — города, куда можно уехать с остановки 100
 * - getCitiesTo() — все возможные города назначения
 */
async function getCitiesTo(options?: IGetFromOrToOptions): Promise<ICityResGet[]> {
    const params = mapOptionsToParams("ГОРОДА_КУДА", options);
    return getFromOrToCities(params);
}

/**
 * Получить остановки ОТКУДА (ОП_ОТКУДА)
 *
 * @template T — флаг адаптации:
 *  - false → возвращает массив сырых точек [{ id, name }]
 *  - true  → возвращает остановки, адаптированные для формы (IFormStation[])
 *
 * @param options - параметры фильтрации GET_FROM_OR_TO
 * @param adaptToForm - если true → выполнить адаптацию через adapterStaitionForForm
 *
 * @example
 * // Сырые точки
 * const raw = await getStopsFrom({ localityIdFrom: "5" });
 *
 * @example
 * // Остановки в формате старой формы
 * const formStations = await getStopsFrom({ localityIdFrom: "5" }, true);
 */
async function getStopsFrom<T extends boolean = false>(
    options?: IGetFromOrToOptions,
    adaptToForm: T = false as T
): Promise<StopsReturn<T>> {
    const params = mapOptionsToParams("ОП_ОТКУДА", options);
    const data = await getFromOrTo(params);

    if (!adaptToForm) {
        return data as StopsReturn<T>;
    }

    // adaptToForm === true
    const adapted = adapterStaitionForForm(data);
    return adapted as unknown as StopsReturn<T>;
}

/**
 * Получить остановки КУДА (ОП_КУДА)
 *
 * @template T — флаг адаптации:
 *  - false → возвращает [{ id, name }]
 *  - true  → возвращает адаптированные IFormStation[]
 *
 * @param options - фильтры GET_FROM_OR_TO
 * @param adaptToForm - если true → выполнить адаптацию
 *
 * @example
 * const raw = await getStopsTo({ localityIdTo: "5" });
 *
 * @example
 * const formStations = await getStopsTo({ localityIdTo: "5" }, true);
 */
async function getStopsTo<T extends boolean = false>(
    options?: IGetFromOrToOptions,
    adaptToForm: T = false as T
): Promise<StopsReturn<T>> {

    const params = mapOptionsToParams("ОП_КУДА", options);
    const data = await getFromOrTo(params);

    if (!adaptToForm) {
        return data as StopsReturn<T>;
    }
    const adapted = adapterStaitionForForm(data);
    return adapted as unknown as StopsReturn<T>;
}

export {
    getFromOrTo,
    getCitiesFrom,
    getCitiesTo,
    getStopsFrom,
    getStopsTo
};